import React from "react";
import { motion } from "framer-motion";
import {
  Infinity as InfinityIcon,
  BadgeCheck,
  HeartHandshake,
  PhoneCall,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { FEATURE_META } from "../../data/landing";
import { useLanguage } from "../../i18n/LanguageContext";
import StoreButtons from "./StoreButtons";

const ICONS = {
  Infinity: InfinityIcon,
  BadgeCheck,
  HeartHandshake,
  PhoneCall,
  MessageCircle,
  ShieldCheck,
};

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const WhyHariOmBol = () => {
  const { t } = useLanguage();
  const w = t.why;

  return (
    <section
      id="features"
      className="relative py-24 md:py-32"
      data-testid="why-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="text-xs font-bold uppercase tracking-[0.16em] text-[#7B1113]"
          >
            {w.label}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 text-3xl font-medium leading-tight tracking-tight text-[#2A1112] md:text-4xl lg:text-5xl"
            data-testid="why-heading"
          >
            {w.heading}
          </motion.h2>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {FEATURE_META.map((meta, i) => {
            const Icon = ICONS[meta.icon];
            const feature = w.features[i];
            return (
              <motion.article
                key={meta.num}
                custom={i}
                variants={cardVariant}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                whileHover={{ y: -8 }}
                className="group relative overflow-hidden rounded-3xl bg-white p-8 shadow-[0_24px_48px_-12px_rgba(123,17,19,0.08)] transition-shadow duration-300 hover:shadow-[0_32px_64px_-16px_rgba(123,17,19,0.16)]"
                data-testid={`feature-card-${meta.num}`}
              >
                <span className="pointer-events-none absolute -right-1 top-1 select-none text-7xl font-black tracking-tighter text-[#7B1113]/[0.05] md:text-8xl">
                  {meta.num}
                </span>

                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-[#7B1113]/8 text-[#7B1113] transition-colors duration-300 group-hover:bg-[#7B1113] group-hover:text-[#F5A623]">
                  <Icon className="h-7 w-7" strokeWidth={1.8} />
                </div>

                <h3 className="relative mt-6 text-xl font-semibold tracking-tight text-[#2A1112]">
                  {feature.title}
                </h3>
                <p className="relative mt-3 text-[15px] leading-relaxed text-[#5E4646]">
                  {feature.desc}
                </p>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 flex flex-col items-center gap-6 text-center"
          data-testid="why-cta"
        >
          <p className="text-lg font-medium text-[#2A1112]">{w.cta}</p>
          <StoreButtons align="center" testidPrefix="why" />
        </motion.div>
      </div>
    </section>
  );
};

export default WhyHariOmBol;
